import { MYINFO_ATTRIBUTE_MAP } from '~shared/constants/field/myinfo'
import {
  AttachmentSize,
  BasicField,
  FieldCreateDto,
  MyInfoAttribute,
  MyInfoChildAttributes,
  MyInfoField,
  RatingShape,
  ReminderAction,
  ReminderFrequency,
  ReminderMode,
  ReminderTrigger,
} from '~shared/types/field'

import { BASICFIELD_TO_DRAWER_META } from '../../constants'
import {
  MYINFO_CHILDRENFIELD_META,
  MYINFO_DATEFIELD_META,
  MYINFO_DROPDOWNFIELD_META,
  MYINFO_MOBILEFIELD_META,
  MYINFO_TEXTFIELD_META,
} from '../constants'

import { createShortTextColumn } from './columnCreation'

/**
 * Utility methods to create bare minimum meta required for field creation.
 */
export const getFieldCreationMeta = (fieldType: BasicField): FieldCreateDto => {
  const baseMeta: Pick<
    FieldCreateDto,
    'description' | 'disabled' | 'required' | 'title'
  > = {
    description: '',
    disabled: false,
    required: true,
    title: BASICFIELD_TO_DRAWER_META[fieldType].label,
  }

  switch (fieldType) {
    case BasicField.Attachment: {
      return {
        fieldType,
        ...baseMeta,
        attachmentSize: AttachmentSize.OneMb,
      }
    }
    case BasicField.Nric: {
      return {
        fieldType,
        ...baseMeta,
        allowPrefill: false,
        lockPrefill: false,
      }
    }
    case BasicField.YesNo: {
      return {
        fieldType,
        yesLabel: 'Yes',
        noLabel: 'No',
        ...baseMeta,
      }
    }
    case BasicField.Uen:
    case BasicField.Section:
    case BasicField.Statement: {
      return {
        fieldType,
        ...baseMeta,
      }
    }
    case BasicField.Checkbox: {
      return {
        fieldType,
        ...baseMeta,
        ValidationOptions: {
          customMax: null,
          customMin: null,
        },
        validateByValue: false,
        fieldOptions: ['Option 1', 'Option 2'],
        othersRadioButton: false,
        othersTitle: 'Others',
        enableSelectAll: false,
        selectAllTitle: 'Select All',
        fieldScores: [10, 20],
        scoring: false,
      }
    }
    case BasicField.Mobile: {
      return {
        fieldType,
        ...baseMeta,
        isVerifiable: false,
        allowIntlNumbers: false,
        defaultCountry: 'SG',
        allowPrefill: false,
        lockPrefill: false,
      }
    }
    case BasicField.HomeNo: {
      return {
        fieldType,
        ...baseMeta,
        allowIntlNumbers: false,
        defaultCountry: 'SG',
        allowPrefill: false,
        lockPrefill: false,
      }
    }
    case BasicField.ShortText: {
      return {
        fieldType,
        ...baseMeta,
        ValidationOptions: {
          selectedValidation: null,
          customVal: null,
        },
        allowPrefill: false,
        lockPrefill: false,
      }
    }
    case BasicField.LongText:
      return {
        fieldType,
        ...baseMeta,
        ValidationOptions: {
          selectedValidation: null,
          customVal: null,
        },
      }
    case BasicField.Number: {
      return {
        fieldType,
        ...baseMeta,
        ValidationOptions: {
          selectedValidation: null,
          LengthValidationOptions: {
            selectedLengthValidation: null,
            customVal: null,
          },
          RangeValidationOptions: {
            customMin: null,
            customMax: null,
          },
        },
      }
    }
    case BasicField.Dropdown: {
      return {
        fieldType,
        ...baseMeta,
        fieldOptions: ['Option 1', 'Option 2'],
        fieldScores: [10, 20],
        scoring: false,
      }
    }
    case BasicField.BotMDDropdown: {
      return {
        fieldType,
        ...baseMeta,
        dataType: '',
      }
    }
    case BasicField.Image: {
      return {
        fieldType,
        ...baseMeta,
        fileMd5Hash: '',
        name: '',
        url: '',
        size: '',
      }
    }
    case BasicField.Decimal: {
      return {
        fieldType,
        ...baseMeta,
        validateByValue: false,
        ValidationOptions: {
          customMin: null,
          customMax: null,
        },
      }
    }
    case BasicField.Email: {
      return {
        fieldType,
        ...baseMeta,
        isVerifiable: false,
        hasAllowedEmailDomains: false,
        allowedEmailDomains: [],
        autoReplyOptions: {
          hasAutoReply: false,
          autoReplySubject: '',
          autoReplyMessage: '',
          autoReplySender: '',
          includeFormSummary: false,
        },
        allowPrefill: false,
        lockPrefill: false,
      }
    }
    case BasicField.Radio: {
      return {
        fieldType,
        ...baseMeta,
        fieldOptions: ['Option 1', 'Option 2'],
        fieldScores: [10, 20],
        scoring: false,
        othersRadioButton: false,
        othersTitle: 'Others',
      }
    }
    case BasicField.Rating: {
      return {
        fieldType,
        ...baseMeta,
        ratingOptions: {
          shape: RatingShape.Star,
          steps: 5,
        },
      }
    }
    case BasicField.Reminder: {
      return {
        fieldType,
        ...baseMeta,
        reminderOptions: {
          mode: ReminderMode.Recurring,
          action: ReminderAction.SendTemplate,
          startTrigger: ReminderTrigger.Enrollment,
          endTrigger: ReminderTrigger.Discharge,
          frequency: ReminderFrequency.Daily,
          messageTemplate: '',
        },
      }
    }
    case BasicField.Date: {
      return {
        fieldType,
        ...baseMeta,
        dateValidation: {
          customMaxDate: null,
          customMinDate: null,
          selectedDateValidation: null,
        },
        allowPrefill: false,
        lockPrefill: false,
      }
    }
    case BasicField.Table: {
      return {
        fieldType,
        ...baseMeta,
        columns: [createShortTextColumn()],
        minimumRows: 2,
      }
    }
    case BasicField.CountryRegion: {
      return {
        fieldType,
        ...baseMeta,
        fieldOptions: [],
      }
    }
    case BasicField.Children: {
      return {
        fieldType,
        ...baseMeta,
      }
    }
  }
}

export const getMyInfoFieldCreationMeta = (
  myInfoAttribute: Exclude<
    MyInfoAttribute,
    MyInfoChildAttributes[keyof MyInfoChildAttributes]
  >,
): MyInfoField => {
  const baseMeta: Pick<
    MyInfoField,
    'disabled' | 'required' | 'title' | 'description' | 'fieldType' | 'myInfo'
  > = {
    disabled: false,
    required: true,
    title: MYINFO_ATTRIBUTE_MAP[myInfoAttribute].value,
    description: '',
    fieldType: MYINFO_ATTRIBUTE_MAP[myInfoAttribute].fieldType,
    myInfo: {
      attr: myInfoAttribute,
    },
  }

  switch (myInfoAttribute) {
    case MyInfoAttribute.Name:
    case MyInfoAttribute.PassportNumber:
    case MyInfoAttribute.VehicleNo:
    case MyInfoAttribute.RegisteredAddress:
    case MyInfoAttribute.MarriageCertNo:
    case MyInfoAttribute.Employment: {
      return {
        ...baseMeta,
        fieldType: BasicField.ShortText,
        ...MYINFO_TEXTFIELD_META,
      }
    }

    case MyInfoAttribute.DateOfBirth:
    case MyInfoAttribute.PassportExpiryDate:
    case MyInfoAttribute.WorkpassExpiryDate:
    case MyInfoAttribute.MarriageDate:
    case MyInfoAttribute.DivorceDate: {
      return {
        ...baseMeta,
        fieldType: BasicField.Date,
        ...MYINFO_DATEFIELD_META,
      }
    }

    case MyInfoAttribute.Sex:
    case MyInfoAttribute.Race:
    case MyInfoAttribute.Nationality:
    case MyInfoAttribute.BirthCountry:
    case MyInfoAttribute.ResidentialStatus:
    case MyInfoAttribute.Dialect:
    case MyInfoAttribute.HousingType:
    case MyInfoAttribute.HdbType:
    case MyInfoAttribute.Occupation:
    case MyInfoAttribute.WorkpassStatus:
    case MyInfoAttribute.Marital:
    case MyInfoAttribute.CountryOfMarriage: {
      return {
        ...baseMeta,
        fieldType: BasicField.Dropdown,
        ...MYINFO_DROPDOWNFIELD_META,
      }
    }

    case MyInfoAttribute.MobileNo: {
      return {
        ...baseMeta,
        fieldType: BasicField.Mobile,
        ...MYINFO_MOBILEFIELD_META,
      }
    }

    case MyInfoAttribute.ChildrenBirthRecords: {
      return {
        ...baseMeta,
        fieldType: BasicField.Children,
        ...MYINFO_CHILDRENFIELD_META,
      }
    }

    default: {
      const exception: never = myInfoAttribute
      throw new Error(`MyInfo type is not implemented: ${exception}`)
    }
  }
}
